/**
 * Paleta de packs por familia visual: la ÚNICA fuente para el bloque de precios
 * de las landings (`LandingPricing`) y para las superficies de compra de la app
 * (selector de packs y modal de checkout, vía `BRAND_PAYWALL` en `lib/brand.ts`).
 *
 * Cada marca del registro apunta aquí con `theme.packVariant` (el registro no
 * puede importar: lo carga el middleware edge), así que añadir una marca es una
 * clave, no dos bloques de colores que divergen.
 *
 * Contrato de los tokens: clases Tailwind COMPLETAS (nunca construidas
 * dinámicamente). Cada superficie lleva su `bg-gradient-*` y, si tiene borde,
 * el COLOR del borde; el grosor (`border`, `border-2`…) y el radio los pone el
 * consumidor, que es quien conoce su tamaño.
 */
export type PackVariant = 'santa' | 'reyes' | 'hada' | 'raton-perez' | 'generic' | 'easter' | 'halloween' | 'birthday'

export interface PackPalette {
  /** Sección de precios de una landing: translúcida sobre el gradiente de la página. */
  section: string
  /** Diálogo del selector de packs y modal de checkout: opaco sobre el overlay. */
  dialog: string
  /** Tarjeta del pack de entrada (el 3): gradiente y color de borde. */
  pack3Card: string
  /** Chip («popular», «ideal para…») del pack de entrada. */
  pack3Badge: string
  /** Color de los checks de la lista de ventajas. */
  check: string
  /** Adorno de tarjetas y diálogos: UN solo glifo (copo, destello…), se pinta suelto. */
  decor: string
}

/**
 * Tarjeta del pack destacado (el 6), constante entre variantes: su identidad
 * visual es «el pack grande», no la del personaje ni la de la marca.
 */
export const PACK6_STYLE = {
  card: 'bg-gradient-to-br from-amber-700/60 to-orange-800/60 border-amber-400/50',
  badge: 'bg-amber-400 text-amber-900',
  cta: 'bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 shadow-xl',
} as const

/** CTA del pack de entrada: cristal neutro que deja ver la paleta de la variante. */
export const PACK3_CTA = 'bg-white/20 hover:bg-white/30 border-2 border-white/40 shadow-lg'

export const PACK_STYLES: Record<PackVariant, PackPalette> = {
  // Rojo y verde navideños con copos: los valores que el paywall llevaba inline hasta 2026-09.
  santa: {
    section: 'bg-gradient-to-br from-red-900/40 to-red-800/40 border-white/10',
    dialog: 'bg-gradient-to-br from-red-900/95 to-green-900/95',
    pack3Card: 'bg-gradient-to-br from-red-700/60 to-red-800/60 border-white/30',
    pack3Badge: 'bg-emerald-400 text-green-900',
    check: 'text-emerald-300',
    decor: '❄',
  },
  reyes: {
    section: 'bg-gradient-to-br from-slate-900/60 via-blue-900/50 to-amber-900/30 border-white/15',
    dialog: 'bg-gradient-to-br from-slate-900/95 to-blue-900/95',
    pack3Card: 'bg-gradient-to-br from-blue-900/70 via-slate-900/80 to-amber-700/40 border-amber-200/30',
    pack3Badge: 'bg-amber-300 text-amber-900',
    check: 'text-amber-300',
    decor: '✶',
  },
  hada: {
    section: 'bg-gradient-to-br from-violet-900/60 via-indigo-900/50 to-purple-900/30 border-white/15',
    dialog: 'bg-gradient-to-br from-violet-900/95 to-indigo-900/95',
    pack3Card: 'bg-gradient-to-br from-violet-800/70 via-indigo-900/80 to-purple-700/40 border-violet-200/30',
    pack3Badge: 'bg-violet-300 text-violet-900',
    check: 'text-violet-300',
    decor: '✨',
  },
  // Ámbar sobre piedra (CHARACTER_THEMES['raton-perez']); el pack de entrada va en
  // piedra para no confundirse con el destacado, ámbar/naranja en todas las variantes.
  'raton-perez': {
    section: 'bg-gradient-to-br from-amber-950/60 via-stone-950/50 to-amber-900/30 border-white/15',
    dialog: 'bg-gradient-to-br from-amber-950/95 to-stone-900/95',
    pack3Card: 'bg-gradient-to-br from-stone-700/70 via-stone-900/80 to-amber-800/40 border-amber-200/30',
    pack3Badge: 'bg-amber-300 text-amber-950',
    check: 'text-amber-300',
    decor: '✦',
  },
  generic: {
    section: 'bg-gradient-to-br from-slate-900/60 via-indigo-900/50 to-sky-900/30 border-white/15',
    dialog: 'bg-gradient-to-br from-slate-900/95 to-indigo-900/95',
    pack3Card: 'bg-gradient-to-br from-indigo-900/70 via-slate-900/80 to-sky-700/40 border-sky-200/30',
    pack3Badge: 'bg-sky-300 text-sky-900',
    check: 'text-sky-300',
    decor: '✧',
  },
  easter: {
    section: 'bg-gradient-to-br from-teal-900/60 via-emerald-900/50 to-lime-900/30 border-white/15',
    dialog: 'bg-gradient-to-br from-teal-900/95 to-emerald-900/95',
    pack3Card: 'bg-gradient-to-br from-emerald-800/70 via-teal-900/80 to-lime-700/40 border-lime-200/30',
    pack3Badge: 'bg-lime-300 text-emerald-900',
    check: 'text-lime-300',
    decor: '❀',
  },
  halloween: {
    section: 'bg-gradient-to-br from-purple-950/60 via-violet-900/50 to-orange-900/30 border-white/15',
    dialog: 'bg-gradient-to-br from-purple-950/95 to-violet-900/95',
    pack3Card: 'bg-gradient-to-br from-violet-900/70 via-purple-950/80 to-orange-700/40 border-orange-200/30',
    pack3Badge: 'bg-orange-300 text-purple-900',
    check: 'text-orange-300',
    decor: '✦',
  },
  birthday: {
    section: 'bg-gradient-to-br from-rose-950/60 via-fuchsia-900/50 to-amber-900/30 border-white/15',
    dialog: 'bg-gradient-to-br from-rose-950/95 to-fuchsia-900/95',
    pack3Card: 'bg-gradient-to-br from-fuchsia-900/70 via-rose-950/80 to-amber-700/40 border-amber-200/30',
    pack3Badge: 'bg-amber-300 text-rose-900',
    check: 'text-amber-300',
    decor: '✻',
  },
}
